import React, { useState, useRef } from "react";
import { Loader2, ScanLine } from "lucide-react";
import { useAppContext } from "../context/AppContext";

interface MedicalScannerProps {
  draft: any;
  setDraft: React.Dispatch<React.SetStateAction<any>>;
}

export default function MedicalScanner({
  draft,
  setDraft,
}: MedicalScannerProps) {
  const { t, lang } = useAppContext();
  const [isScanning, setIsScanning] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleMedicalScan = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsScanning(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        let w = img.width,
          h = img.height;

        if (w > h) {
          if (w > 800) {
            h *= 800 / w;
            w = 800;
          }
        } else {
          if (h > 800) {
            w *= 800 / h;
            h = 800;
          }
        }

        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d")?.drawImage(img, 0, 0, w, h);

        const base64Image = canvas.toDataURL("image/jpeg", 0.8);
        const localKey = localStorage.getItem("mypaw_gemini_key");

        if (
          window.location.hostname.includes("csb.app") ||
          window.location.hostname.includes("localhost")
        ) {
          if (!localKey) {
            setTimeout(() => {
              setDraft((prev: any) => ({
                ...prev,
                features:
                  (prev.features ? prev.features + "\n\n" : "") +
                  "✅ [ТЕСТОВИЙ РЕЖИМ]: Додайте API ключ в налаштуваннях.",
              }));
              setIsScanning(false);
              if (fileRef.current) fileRef.current.value = "";
            }, 2000);
            return;
          }
        }

        try {
          let data;
          const strictPrompt = `${
            t.scanPrompt || "Analyze this medical document."
          }
CRITICAL INSTRUCTION: You are a medical data extractor. Return ONLY a raw JSON object. Do not use Markdown blocks (\`\`\`json). Do not add any conversational text.
The JSON must strictly follow this exact structure:
{
  "diagnoses": ["short tag 1"],
  "meds": ["med 1"],
  "allergies": ["allergy 1"],
  "summary": "Short text summary"
}
Extract keywords in the same language as the document. 
RULE: Keep items in the arrays extremely short (1 to 3 words maximum), like tags!`;

          if (localKey && localKey.trim().length > 0) {
            const res = await fetch(
              "https://api.groq.com/openai/v1/chat/completions",
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${localKey.trim()}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  model: "llama-3.2-11b-vision-preview",
                  messages: [
                    {
                      role: "user",
                      content: [
                        { type: "text", text: strictPrompt },
                        { type: "image_url", image_url: { url: base64Image } },
                      ],
                    },
                  ],
                  max_tokens: 1000,
                  temperature: 0.1,
                }),
              }
            );
            data = await res.json();
            if (!res.ok) throw new Error(data.error?.message || "AI API Error");
          } else {
            const res = await fetch("/api/scan-medical", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                prompt: strictPrompt,
                imageBase64: base64Image,
              }),
            });
            data = await res.json();
            if (data.error) throw new Error(data.error);
          }

          const rawText = data.choices[0]?.message?.content || "";
          const jsonMatch = rawText.match(/\{[\s\S]*\}/);

          if (!jsonMatch)
            throw new Error("Не вдалося знайти JSON у відповіді AI");

          const parsed = JSON.parse(jsonMatch[0]);

          setDraft((prev: any) => ({
            ...prev,
            diagnoses: Array.from(
              new Set([
                ...(prev.diagnoses || []),
                ...(parsed.diagnoses || []).filter(Boolean),
              ])
            ),
            meds: Array.from(
              new Set([
                ...(prev.meds || []),
                ...(parsed.meds || []).filter(Boolean),
              ])
            ),
            allergies: Array.from(
              new Set([
                ...(prev.allergies || []),
                ...(parsed.allergies || []).filter(Boolean),
              ])
            ),
            features:
              (prev.features ? prev.features + "\n\n" : "") +
              (t.scanResult || "Результат:") +
              "\n" +
              (parsed.summary || ""),
          }));
        } catch (err: any) {
          alert(
            lang === "uk"
              ? "Помилка аналізу. Спробуйте ще раз або перевірте API ключ."
              : "Scan error. Check your API key."
          );
          console.error("Сканер AI помилка:", err);
        } finally {
          setIsScanning(false);
          if (fileRef.current) fileRef.current.value = "";
        }
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={isScanning}
        className="flex items-center gap-1 text-[10px] font-bold text-[#a073e7] bg-[var(--accent-soft)] px-2 py-1 rounded-lg active:scale-95 transition-all"
      >
        {isScanning ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <ScanLine size={12} />
        )}
        {isScanning ? t.analyzing : t.aiScanner}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleMedicalScan}
      />
    </>
  );
}
